import { useCallback, useEffect, useRef, useState } from 'react';
import { registerPendingWork } from './pendingWork';

/** Phones and tablets, where a hidden page may be frozen or discarded without warning. */
const isTouchFirst = () => typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches;

/**
 * Holds actions back for a short undo window before committing them.
 *
 * Used where an Undo can't be implemented as a reversing write: approving
 * leave notifies the requester and recomputes allocations, and Firestore
 * rules only let a user add (never remove) their own uid on a notification.
 *
 * Nothing held is dropped silently:
 * - closing or reloading the tab asks the user to confirm first, and staying
 *   keeps the Undo (a write started from that prompt would not survive it);
 *   leaving anyway starts the commits on `pagehide`, as a best effort;
 * - on touch-first devices, hiding the tab (switching apps, locking the
 *   phone) commits immediately, since a backgrounded page can be frozen or
 *   killed; elsewhere the window keeps running while the tab is hidden;
 * - sign-out awaits every held action through flushPendingWork.
 */
export const useDeferredActions = <T>(
  delayMs: number,
  commit: (id: string, payload: T) => void | Promise<unknown>
) => {
  const [pending, setPending] = useState<Record<string, T>>({});
  const payloads = useRef(new Map<string, T>());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // Commits already started (their window ended), so sign-out can wait for them too.
  const inFlight = useRef(new Set<Promise<unknown>>());
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const forget = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    payloads.current.delete(id);
    setPending((current) => {
      if (!(id in current)) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  }, []);

  const run = useCallback(
    async (id: string): Promise<void> => {
      if (!payloads.current.has(id)) return;
      const payload = payloads.current.get(id) as T;
      forget(id);
      const write = Promise.resolve(commitRef.current(id, payload));
      inFlight.current.add(write);
      try {
        await write;
      } finally {
        inFlight.current.delete(write);
      }
    },
    [forget]
  );

  const schedule = useCallback(
    (id: string, payload: T) => {
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);
      payloads.current.set(id, payload);
      timers.current.set(
        id,
        setTimeout(() => {
          void run(id);
        }, delayMs)
      );
      setPending((current) => ({ ...current, [id]: payload }));
    },
    [delayMs, run]
  );

  /** Commits everything still held and waits for every commit already under way. */
  const flushAll = useCallback(async (): Promise<void> => {
    const started = Array.from(payloads.current.keys()).map(run);
    await Promise.allSettled([...started, ...Array.from(inFlight.current)]);
  }, [run]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (payloads.current.size === 0) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isTouchFirst()) void flushAll();
    };
    const handlePageHide = () => {
      void flushAll();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const unregister = registerPendingWork(
      flushAll,
      () => payloads.current.size > 0 || inFlight.current.size > 0
    );
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      unregister();
    };
  }, [flushAll]);

  useEffect(() => {
    const activeTimers = timers.current;
    return () => activeTimers.forEach((timer) => clearTimeout(timer));
  }, []);

  return { pending, schedule, cancel: forget, flushAll };
};
